import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  PhoneIncoming, ShieldCheck, Clock, MessageCircle, Radio, Voicemail,
  Hash, PhoneCall, MessageSquare, CheckCircle2, XCircle,
} from "lucide-react";

export default function IVR() {
  const { user } = useAuth();
  const { current, reloadWorkspace } = useWorkspace();
  const [ws, setWs] = useState(null);

  useEffect(() => { api.get("/workspace").then((r) => setWs(r.data)); }, [current]);

  if (!ws) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const ivr = ws.ivr || {};
  const bh = ws.business_hours || {};

  const channels = [
    { name: "Voice (Exotel)", icon: PhoneCall, number: ws.exotel?.number, connected: ws.exotel?.connected, provider: "Exotel" },
    { name: "WhatsApp Business", icon: MessageCircle, number: ws.waba?.number, connected: ws.waba?.connected, provider: ws.waba?.provider },
    { name: "SMS (Exotel)", icon: MessageSquare, number: ws.exotel?.number, connected: ws.exotel?.connected, provider: "Exotel" },
    { name: "Meta Lead Ads (FB & Insta)", icon: Radio, number: "Webhook Active", connected: true, provider: "Meta Ads" },
  ];

  return (
    <div>
      <PageHeader eyebrow="Telephony & Channels" title="Light IVR & Channel Setup"
        subtitle="Greeting, consent, menu routing and after-hours handling" />
      <div className="p-4 sm:p-8 grid lg:grid-cols-3 gap-5">
        {/* IVR flow */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 bg-card/40 border-border/60">
            <p className="eyebrow text-gold mb-4 flex items-center gap-2"><Radio className="w-4 h-4" /> Inbound Call Flow</p>
            <FlowStep icon={PhoneIncoming} title="1 · Greeting" color="bg-blue-500/15 text-blue-300" body={ivr.greeting} />
            <Connector />
            <FlowStep icon={ShieldCheck} title="2 · Consent announcement (India legal)" color="bg-gold/15 text-gold" body={ivr.consent} />
            <Connector />
            <div className="rounded-xl border border-border/60 bg-card/40 p-4">
              <div className="flex items-center gap-2 mb-3"><span className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center"><Hash className="w-4 h-4" /></span><p className="text-sm font-medium">3 · Menu → routed to available associate partner</p></div>
              <div className="space-y-1.5 ml-10">
                {(ivr.menu || []).map((m) => (
                  <div key={m.key} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className="border-gold/40 text-gold font-mono w-7 justify-center">{m.key}</Badge>
                    <span>{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <Connector />
            <FlowStep icon={Voicemail} title="4 · After-hours / no associate partner" color="bg-amber-500/15 text-amber-300"
              body="Voicemail captured + auto WhatsApp reply sent + callback logged as a high-priority ticket." />
          </Card>
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <Card className="p-5 bg-card/40 border-border/60">
            <p className="eyebrow text-gold mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Business Hours</p>
            <p className="text-2xl font-serif text-white">09:00 – 21:00</p>
            <p className="text-sm text-muted-foreground">Monday – Saturday · IST</p>
            <p className="text-xs text-muted-foreground mt-2">Outside these hours the after-hours flow activates automatically.</p>
          </Card>

          <Card className="p-5 bg-card/40 border-border/60">
            <p className="eyebrow text-gold mb-3">Connected Channels</p>
            <div className="space-y-3">
              {channels.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.name} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/40 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-gold" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground font-mono truncate">{c.provider} · {c.number}</p>
                    </div>
                    {c.connected ? (
                      <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 gap-1 text-[10px]"><CheckCircle2 className="w-3 h-3" /> Live</Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-400/50 text-amber-300 gap-1 text-[10px]"><XCircle className="w-3 h-3" /> Simulated</Badge>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
              Webhooks are wired and ready. Add your Exotel & {ws.waba?.provider} credentials to switch from simulation to live.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FlowStep({ icon: Icon, title, body, color }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="flex items-center gap-2 mb-2"><span className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></span><p className="text-sm font-medium">{title}</p></div>
      <p className="text-sm text-muted-foreground ml-10 leading-relaxed">{body}</p>
    </div>
  );
}
function Connector() {
  return <div className="ml-8 h-4 w-px bg-gold/30 my-1" />;
}
